import React, { useEffect, useState } from "react";
import { Mail, Loader2, AlertTriangle, Inbox, CheckCircle2, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import api, { readError } from "@/lib/api";

const STATUS_TABS = ["all", "new", "read", "replied"];
const STATUS_LABEL = { all: "All", new: "New", read: "Read", replied: "Replied" };
const STATUS_COLOR = {
  new: "bg-primary text-primary-foreground",
  read: "bg-secondary/30 text-secondary",
  replied: "bg-muted text-muted-foreground",
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("hr-HR", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

export default function AdminMessages() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setError("");
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data } = await api.get("/admin/messages", { params });
      setItems(data.items || []);
    } catch (err) {
      setError(readError(err, "Could not load messages."));
    }
  };

  useEffect(() => { load(); }, [filter]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/messages/${id}`, { status });
      toast.success(`Marked as ${status}`);
      // optimistic update
      setActive((m) => (m && m.id === id ? { ...m, status } : m));
      await load();
    } catch (err) {
      toast.error(readError(err, "Could not update status."));
    }
  };

  return (
    <div className="space-y-7 animate-fade-up">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-primary">Inbox</div>
        <h1 className="mt-1 font-serif text-4xl text-foreground">Messages</h1>
        <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
          Contact form submissions from the public site.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="messages-filter-tabs">
        {STATUS_TABS.map((s) => (
          <button
            key={s}
            type="button"
            data-testid={`messages-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`px-3.5 py-1.5 rounded-full text-sm transition-colors ${
              filter === s ? "bg-primary text-primary-foreground shadow-interactive" : "border border-border text-foreground/70 hover:text-primary hover:border-primary/40"
            }`}
          >
            {STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5" strokeWidth={1.5} /> {error}
        </div>
      )}

      {items === null ? (
        <div className="p-10 flex items-center justify-center text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…</div>
      ) : items.length === 0 ? (
        <Card className="bg-card border-border/60 shadow-ambient">
          <CardContent className="p-10 text-center">
            <Inbox className="h-10 w-10 mx-auto text-muted-foreground/40" strokeWidth={1.5} />
            <p className="mt-3 text-sm text-muted-foreground">No messages here.</p>
          </CardContent>
        </Card>
      ) : (
        <ul className="space-y-2" data-testid="messages-list">
          {items.map((m) => (
            <li key={m.id}>
              <button
                type="button"
                data-testid={`message-row-${m.id}`}
                onClick={() => {
                  setActive(m);
                  if (m.status === "new") setStatus(m.id, "read");
                }}
                className="w-full text-left rounded-lg bg-card border border-border/60 hover:border-primary/40 hover:shadow-interactive transition-all p-4 flex items-start gap-3"
              >
                <div className="rounded-lg bg-primary/10 p-2 mt-0.5">
                  <Mail className="h-4 w-4 text-primary" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <div className="font-serif text-base text-foreground truncate">{m.subject}</div>
                    <div className="text-xs text-muted-foreground whitespace-nowrap">{fmt(m.created_at)}</div>
                  </div>
                  <div className="mt-0.5 text-sm text-foreground/75 truncate">{m.name} · {m.email}</div>
                  <div className="mt-1 text-sm text-foreground/70 line-clamp-1">{m.message}</div>
                </div>
                <Badge className={STATUS_COLOR[m.status]}>{m.status}</Badge>
              </button>
            </li>
          ))}
        </ul>
      )}

      <Sheet open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <SheetContent className="bg-card border-border max-w-md w-full overflow-y-auto" data-testid="message-detail-sheet">
          {active && (
            <>
              <SheetHeader>
                <div className="text-[10px] uppercase tracking-[0.2em] text-primary mb-1">From {active.locale?.toUpperCase()}</div>
                <SheetTitle className="font-serif text-2xl">{active.subject}</SheetTitle>
                <SheetDescription className="space-y-1">
                  <div className="text-sm text-foreground">{active.name}</div>
                  <a href={`mailto:${active.email}`} className="text-sm text-primary hover:underline break-all">{active.email}</a>
                  <div className="text-xs text-muted-foreground">{fmt(active.created_at)} · {active.ip}</div>
                </SheetDescription>
              </SheetHeader>
              <div className="mt-6 prose-tz text-sm text-foreground/85 leading-relaxed whitespace-pre-wrap" data-testid="message-body">
                {active.message}
              </div>
              <SheetFooter className="mt-7 gap-2 flex-wrap">
                <a href={`mailto:${active.email}?subject=Re: ${encodeURIComponent(active.subject)}`} className="inline-flex" data-testid="message-reply">
                  <Button variant="outline">
                    <MessageSquare className="h-3.5 w-3.5" strokeWidth={1.5} /> Reply by email
                  </Button>
                </a>
                {active.status !== "replied" && (
                  <Button onClick={() => setStatus(active.id, "replied")} className="shadow-interactive" data-testid="message-mark-replied">
                    <CheckCircle2 className="h-4 w-4" strokeWidth={1.5} /> Mark replied
                  </Button>
                )}
              </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
